/**
 * SearchableSelect — самописный кастомный <select> с поиском и выпадающим списком.
 * Версия: 1.x
 * Зависимости: jQuery (>= 3.x). Использует MutationObserver (желателен для авто-синхронизации).
 *
 * ───────────────────────────────────────────────────────────────────────────────
 * КРАТКО
 * ───────────────────────────────────────────────────────────────────────────────
 * - Рендерит поверх нативного <select> «кнопку-дисплей» со стрелкой и состояниями
 *   (hover/focus/open/disabled), а также выпадающий список с поиском.
 * - Полная синхронизация со значением нативного <select>:
 *   · внутренняя смена значения вызывает native "change"
 *   · внешняя смена значения/опций/disabled обновляет UI (через MutationObserver + "change")
 * - Клавиатурная навигация: Enter/Space/ArrowUp/ArrowDown/Escape.
 * - Поддержка placeholder (из options или data-placeholder/disabled selected option).
 * - API для программного управления и динамической замены опций.
 *
 * ───────────────────────────────────────────────────────────────────────────────
 * РАЗМЕТКА (пример)
 * ───────────────────────────────────────────────────────────────────────────────
 * <select id="city" data-placeholder="Выберите город">
 *   <option disabled selected></option>
 *   <option value="msk">Москва</option>
 *   <option value="spb">Санкт-Петербург</option>
 *   <option value="ekb">Екатеринбург</option>
 * </select>
 *
 * ───────────────────────────────────────────────────────────────────────────────
 * ИНИЦИАЛИЗАЦИЯ
 * ───────────────────────────────────────────────────────────────────────────────
 * const s = new SearchableSelect($('#city'), {
 *   placeholder: 'Город',   // string | null — текст плейсхолдера; если null, берётся из data-placeholder или disabled selected option
 *   searchInput: true,      // boolean — показывать поле поиска в выпадающем списке
 *   onOpen:  (inst) => {},  // callback — вызывается после открытия
 *   onClose: (inst) => {}   // callback — вызывается после закрытия
 * });
 *
 * ───────────────────────────────────────────────────────────────────────────────
 * ПУБЛИЧНОЕ API
 * ───────────────────────────────────────────────────────────────────────────────
 * .open()                       — программно открыть выпадающий список.
 * .close()                      — закрыть список.
 *
 * .setValue(value, { silent })  — установить значение.
 *   value: string
 *   silent: boolean = false     — если true, не триггерит native "change" на <select>.
 *
 * .enable() / .disable()        — включить/выключить компонент (проксируется в disabled у <select>).
 *
 * .setOptions(list)             — заменить опции <select>.
 *   list: Array<{ value: string, text: string, hidden?: boolean }>
 *   Примечания:
 *     - если задан placeholder (опция/атрибут), сверху сохраняется пустая disabled selected опция;
 *     - пытается сохранить прежнее выбранное значение, если оно осталось в списке;
 *     - после замены обновляет UI и, если список открыт, — содержимое дропдауна.
 *
 * .destroy()                    — корректно удалить компонент:
 *     снимает обработчики/наблюдатели, убирает кастомный UI и возвращает исходный <select>.
 *
 * ───────────────────────────────────────────────────────────────────────────────
 * СОБЫТИЯ
 * ───────────────────────────────────────────────────────────────────────────────
 * - Компонент порождает native "change" на исходном <select> при изменении через UI или .setValue()
 *   (кроме случая setValue(..., { silent: true })).
 * - onOpen(inst), onClose(inst) — коллбеки из опций конструктора.
 *
 * ───────────────────────────────────────────────────────────────────────────────
 * КЛАВИАТУРА И ДОСТУПНОСТЬ
 * ───────────────────────────────────────────────────────────────────────────────
 * - На «кнопке-дисплее»:
 *     Space/Enter — открыть.
 *     ArrowDown   — открыть и подсветить первый пункт.
 *     ArrowUp     — открыть и подсветить последний пункт.
 * - В поле поиска:
 *     ArrowUp/Down — перемещение по пунктам.
 *     Enter        — выбрать подсвеченный.
 *     Escape       — закрыть и вернуть фокус на дисплей.
 * - ARIA: aria-haspopup="listbox", aria-expanded, role="option" на пунктах.
 *
 * ───────────────────────────────────────────────────────────────────────────────
 * СТИЛИ/ХУКИ (CSS-классы)
 * ───────────────────────────────────────────────────────────────────────────────
 * Корень:
 *   .searchable-root             — контейнер компонента
 *   .searchable-root.open        — открыт дропдаун
 *   .searchable-root.disabled    — отключено
 *
 * Нативный select:
 *   .searchable-native           — исходный <select> (визуально скрыт, но участвует в форме)
 *
 * Видимый «селект»:
 *   .searchable-display          — «кнопка-дисплей» текущего значения
 *   .searchable-display.placeholder — состояние, когда показан плейсхолдер
 *   .searchable-caret            — стрелка
 *
 * Дропдаун:
 *   .searchable-container        — всплывающее окно
 *   .searchable-input__wrapper   — обёртка поля поиска
 *   .searchable-input            — input для фильтрации
 *   .searchable-list             — список <ul>
 *   .searchable-list li          — пункт
 *   .searchable-list li.highlight — подсветка при навигации
 *   .searchable-list li.selected  — пункт, соответствующий текущему выбранному value
 *
 * ───────────────────────────────────────────────────────────────────────────────
 * СИНХРОНИЗАЦИЯ И ПОВЕДЕНИЕ
 * ───────────────────────────────────────────────────────────────────────────────
 * - Внешние изменения:
 *     $('#city').val('spb').trigger('change')   → UI обновится.
 *     Добавление/удаление <option> / изменение disabled → UI обновится (MutationObserver).
 * - Внутренние изменения:
 *     Выбор пункта в UI вызывает setValue(value), который меняет <select>.value,
 *     обновляет текст на дисплее и триггерит native "change" (кроме silent=true).
 * - Поиск:
 *     Фильтрует по innerText опций; скрытые option[hidden] пропускаются.
 * - Ширина:
 *     Контейнер наследует ширину исходного <select> при инициализации; дропдаун подгоняется на resize.
 *
 * ───────────────────────────────────────────────────────────────────────────────
 * ПРИМЕРЫ
 * ───────────────────────────────────────────────────────────────────────────────
 * const s = new SearchableSelect($('#city'), { placeholder: 'Город', searchInput: true });
 *
 * // программно выбрать
 * s.setValue('spb');                 // вызовет native "change"
 * s.setValue('msk', { silent: true }); // без события "change"
 *
 * // открыть/закрыть
 * s.open();
 * s.close();
 *
 * // динамически заменить список опций
 * s.setOptions([
 *   { value: 'msk', text: 'Москва' },
 *   { value: 'spb', text: 'Санкт-Петербург' },
 *   { value: 'ekb', text: 'Екатеринбург', hidden: false }
 * ]);
 *
 * // выключить/включить
 * s.disable();
 * s.enable();
 *
 * // корректно удалить компонент
 * // s.destroy();
 *
 * ───────────────────────────────────────────────────────────────────────────────
 * ОГРАНИЧЕНИЯ/ЗАМЕТКИ
 * ───────────────────────────────────────────────────────────────────────────────
 * - Режим multiple/теги — не реализован в этой версии.
 * - Если MutationObserver недоступен, внешние изменения DOM опций могут не отслеживаться;
 *   в этом случае после правок опций вызовите вручную: s.setOptions(...), s.setValue(...),
 *   либо $.trigger('change') на <select>.
 * - При наличии нескольких инстансов рекомендуется неймспейсить глобальные обработчики
 *   (document/window events) — при необходимости адаптируйте внутри класса.
 */


class SearchableSelect {

    static activeInstance = null;

    constructor($select, options) {
        this.opts = {
            onOpen: null,
            onClose: null,
            onSelect: null,
            searchInput: true,
            placeholder: null,
            classes: null
        };
        Object.assign(this.opts, options);

        this.$select = $select;
        this.createDisplay();
        this.createDropdown();
        this.bindEvents();
        this.updateDisplay();

        // NEW: наблюдаем за внешними изменениями select/option
        this.mo = new MutationObserver(() => {
            this.updateDisplay();
            // если открыт — актуализируем список (например, добавили option)
            if (this.$container.is(':visible')) this.refreshList(this.$input.val() || '');
        });
        this.mo.observe(this.$select.get(0), { attributes: true, childList: true, subtree: true });
    }

    /* === НОВОЕ: видимый «селект» (кнопка) и корень === */
    createDisplay() {
        // корневой контейнер
        this.$root = $('<div class="searchable-root"></div>');

        if(this.opts.classes){
            this.$root.addClass(this.opts.classes);
        }

        this.$select.addClass('searchable-native'); // делаем нативный селект "невидимым" стилями из CSS
        this.$select.wrap(this.$root);
        this.$root = this.$select.parent(); // обновим ссылку

        // видимая кнопка-дисплей
        this.$display = $('<div class="searchable-display" tabindex="0" aria-haspopup="listbox" aria-expanded="false"></div>');
        this.$caret   = $('<span class="searchable-caret"><i class="icon-chevron-down"></i></span>');

        this.$display.append(this.$caret);
        this.$root.append(this.$display);

        // если у select есть fixed ширина — унаследуем; иначе пусть width по root
        const w = this.$select.outerWidth();
        //if (w) this.$root.css('width', w);
    }

    /* === ТВОЙ ДРОПДАУН (слегка адаптирован под root) === */
    createDropdown() {
        this.$container = $('<div class="searchable-container" role="listbox"></div>');

        // контейнер после видимого дисплея
        this.$display.after(this.$container);

        this.$inputWrapper = $('<div class="searchable-input__wrapper"><i class="icon-search"></i></div>');
        this.$input = $('<input type="text" class="searchable-input" placeholder="Search...">');
        this.$list = $('<ul class="searchable-list custom-scrollbar"></ul>');

        this.$inputWrapper.append(this.$input);
        if (this.opts.searchInput === true) {
            this.$container.append(this.$inputWrapper);
        }
        this.$container.append(this.$list);
        this.highlightIndex = -1;
    }

    /* === НОВОЕ: обновление текста и placeholder === */
    updateDisplay() {
        const $selOpt = this.$select.find('option:selected');
        let text = $selOpt.length ? $selOpt.text() : '';

        if (!text) {
            if (this.opts.placeholder) {
                text = this.opts.placeholder;
            } else {
                const $ph = this.$select.find('option[disabled][selected]');
                text = $ph.length ? $ph.text() : (this.$select.data('placeholder') || 'Select…');
            }
            this.$display.addClass('placeholder');
        } else {
            this.$display.removeClass('placeholder');
        }

        this.$display.contents().filter(function(){return this.nodeType===3;}).remove();
        this.$caret.before(document.createTextNode(text));

        const isDisabled = this.$select.is(':disabled');
        this.$root.toggleClass('disabled', isDisabled);
        this.$display.attr('aria-disabled', isDisabled ? 'true' : 'false');
    }

    showDropdown() {
        if (SearchableSelect.activeInstance && SearchableSelect.activeInstance !== this) {
            SearchableSelect.activeInstance.hideDropdown();
        }

        this.$root.addClass('open');
        this.$display.attr('aria-expanded', 'true');

        const width = this.$root.innerWidth();
        this.$container.css({ top: 'calc(100% + 6px)', left: 0, width: Number.parseInt(width) }).show();

        this.refreshList();
        if (this.opts.searchInput === true) {
            this.$input.val('').focus();
        } else {
            // фокус в список при отсутствии инпута
            this.$container.attr('tabindex', '-1').focus();
        }

        if (this.opts.onOpen) this.opts.onOpen(this);
        SearchableSelect.activeInstance = this;
    }

    hideDropdown() {
        this.$container.hide();
        this.$root.removeClass('open');
        this.$display.attr('aria-expanded', 'false');

        if (this.opts.onClose) this.opts.onClose(this);
        if (SearchableSelect.activeInstance === this) SearchableSelect.activeInstance = null;
    }

    refreshList(filter = '') {
        this.$list.empty();
        const filterLower = filter.toLowerCase();
        this.$select.find('option').each((i, opt) => {
            const $opt = $(opt);
            if ($opt.prop('hidden')) return;

            const text = $opt.text();
            if (text.toLowerCase().includes(filterLower)) {
                const $item = $(`<li role="option">${text}</li>`);
                $item.data('value', $opt.val());
                if ($opt.is(':selected')) $item.addClass('selected');
                this.$list.append($item);
            }
        });
        this.highlightIndex = -1;
    }

    bindEvents() {
        this.$display.on('mousedown', e => e.preventDefault());
        this.$display.on('click', (e) => {
            if (this.$select.is(':disabled')) return;
            e.stopPropagation();
            this.$container.is(':visible') ? this.hideDropdown() : this.showDropdown();
        });

        this.$display.on('keydown', (e) => {
            if (this.$select.is(':disabled')) return;
            if ([' ', 'Enter', 'ArrowDown', 'ArrowUp'].includes(e.key)) {
                e.preventDefault();
                if (!this.$container.is(':visible')) this.showDropdown();
                const items = this.$list.children('li');
                if (items.length) {
                    if (e.key === 'ArrowDown') { this.highlightIndex = 0; this.updateHighlight(); }
                    if (e.key === 'ArrowUp')   { this.highlightIndex = items.length - 1; this.updateHighlight(); }
                }
            }
        });

        // Внешние изменения value → обновляем дисплей
        this.$select.on('change', () => this.updateDisplay());

        this.$input.on('input', () => this.refreshList(this.$input.val()));
        this.$input.on('keydown', (e) => {
            const items = this.$list.children('li');
            if (e.key === 'ArrowDown') {
                e.preventDefault();
                if (!items.length) return;
                this.highlightIndex = (this.highlightIndex + 1) % items.length;
                this.updateHighlight();
            } else if (e.key === 'ArrowUp') {
                e.preventDefault();
                if (!items.length) return;
                this.highlightIndex = (this.highlightIndex - 1 + items.length) % items.length;
                this.updateHighlight();
            } else if (e.key === 'Enter') {
                e.preventDefault();
                if (this.highlightIndex >= 0) this.selectItem(items.eq(this.highlightIndex));
            } else if (e.key === 'Escape') {
                e.preventDefault();
                this.hideDropdown();
                this.$display.focus();
            }
        });

        this.$list.on('click', 'li', (e) => this.selectItem($(e.currentTarget)));

        $(document).on('click', (e) => {
            if (!$(e.target).closest(this.$root).length) this.hideDropdown();
        });

        $(window).on('resize', () => {
            if (this.$container.is(':visible')) {
                const width = this.$root.innerWidth();
                this.$container.css({ width: Number.parseInt(width) });
            }
        });
    }

    updateHighlight() {
        const $items = this.$list.children('li');
        $items.removeClass('highlight');
        if (this.highlightIndex >= 0) {
            const $item = $items.eq(this.highlightIndex);
            $item.addClass('highlight');
            this.scrollToView($item);
        }
    }

    scrollToView($item) {
        const containerTop = this.$list.scrollTop();
        const containerHeight = this.$list.innerHeight();
        const itemTop = $item.position().top + containerTop;
        const itemHeight = $item.outerHeight();
        if (itemTop < containerTop) {
            this.$list.scrollTop(itemTop);
        } else if (itemTop + itemHeight > containerTop + containerHeight) {
            this.$list.scrollTop(itemTop + itemHeight - containerHeight);
        }
    }

    selectItem($item) {
        const value = $item.data('value');
        this.setValue(value); // используем публичный метод

        if(this.opts.onSelect){
            this.opts.onSelect(value);
        }

        this.hideDropdown();
        this.$display.focus();
    }


    /* ===== ПУБЛИЧНОЕ API ===== */

    open()  { if (!this.$select.is(':disabled')) this.showDropdown(); }
    close() { this.hideDropdown(); }

    setValue(value, { silent = false } = {}) {
        const prev = this.$select.val();
        this.$select.val(value);
        if (!silent && prev !== value) this.$select.trigger('change');
        this.updateDisplay();
        if (this.$container.is(':visible')) this.refreshList(this.$input.val() || '');
    }

    enable()  { this.$select.prop('disabled', false); this.updateDisplay(); }
    disable() { this.$select.prop('disabled', true);  this.updateDisplay(); }

    setOptions(list = []) {
        // list: [{value, text, hidden}]
        const curVal = this.$select.val();
        this.$select.empty();
        // placeholder если был
        const ph = this.opts.placeholder || this.$select.data('placeholder');
        if (ph) this.$select.append(`<option disabled selected></option>`);

        list.forEach(o => {
            const opt = document.createElement('option');
            opt.value = o.value;
            opt.textContent = o.text;
            if (o.hidden) opt.hidden = true;
            this.$select.get(0).appendChild(opt);
        });
        // попытка восстановить прежнее значение
        if (curVal && this.$select.find(`option[value="${curVal}"]`).length) {
            this.$select.val(curVal);
        } else {
            // иначе оставляем placeholder как выбранный (disabled selected)
            if (!this.$select.find('option:selected').length && ph) {
                const first = this.$select.find('option').get(0);
                if (first) { first.disabled = true; first.selected = true; }
            }
        }
        this.updateDisplay();
        if (this.$container.is(':visible')) this.refreshList(this.$input.val() || '');
    }

    destroy() {
        // снять наблюдателей/события и восстановить DOM
        if (this.mo) this.mo.disconnect();
        $(document).off('click'); // если у тебя несколько инстансов, лучше неймспейсить
        $(window).off('resize');

        this.$display.remove();
        this.$container.remove();

        this.$select.removeClass('searchable-native');
        this.$select.unwrap(); // убрать .searchable-root
    }


}

export default SearchableSelect;
